import React from "react";

import { Text } from "../components/text";
import { useI18n } from "../lib/i18n/i18n_provider";

interface StripeUnavailableProps {
  /**
   * Omitted when nothing can be retried in place. The card then states the
   * situation without offering an action that would only fail again.
   */
  onRetry?: () => void;
  /**
   * A retry is in flight. The card stays put with its button disabled, because
   * clearing it would drop the reader back to the empty surface they just asked
   * us to fix.
   */
  retrying?: boolean;
  /**
   * What the reader can do about it. Defaults to the blocked-script advice,
   * which is wrong for failures that originate in the payment attempt rather
   * than in the browser — those pass their own.
   */
  instruction?: React.ReactNode;
}

/**
 * Shown when Stripe cannot be used, in place of whatever it gates.
 *
 * Two different failures share this card: the Stripe.js script never arriving,
 * and a payment element that will never render. A reader cannot tell those
 * apart and shouldn't have to, so the heading is one sentence for both and only
 * the instruction under it changes.
 *
 * For the script, the usual causes sit on the reader's side — an ad blocker, a
 * privacy extension, a filtering network — so they are things they can act on,
 * but only if told. Rendering nothing instead leaves an empty surface that reads
 * as our own outage and offers nothing to try.
 *
 * The wording names the payment provider rather than the payment form, because
 * several surfaces gate an entire page on this script and only some of them are
 * a checkout.
 */
export function StripeUnavailable(props: StripeUnavailableProps) {
  const { onRetry, retrying = false, instruction } = props;
  const { fbt } = useI18n();

  return (
    <div className="d-flex flex-column align-items-center gap-3 py-5">
      <Text as="h2" fontSize="lg" isBold align="center">
        {fbt("We couldn't load our payment provider")}
      </Text>
      {/*
        Not muted, for two reasons that agree. This sentence is the instruction
        — the only thing the reader can act on — so muting it would flatten the
        card's hierarchy to no purpose. And the muted token is one of the few
        the dark-theme partners never remap, unlike the default text color, so
        it would sit near or under the AA floor on their surfaces.
      */}
      <Text align="center">
        {instruction ??
          fbt(
            "An ad blocker, privacy extension, or restricted network can block it. Allow this page, or try a different browser."
          )}
      </Text>
      {onRetry && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={onRetry}
          disabled={retrying}
        >
          {fbt("Try again")}
        </button>
      )}
    </div>
  );
}
